import { useState } from "react";
import { Loader2, Crown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, getUTM, trackLead, errMsg } from "@/lib/api";
import { Field, TextInput, TextArea, SelectField, Pills, Checkbox } from "./fields";
import { validateContact } from "./validators";
import ThankYou from "./ThankYou";
import { toast } from "sonner";

const EMPTY = {
  name: "", phone: "+31 ", email: "", city: "", owns_property: "", interest: "",
  message: "", preferred_channel: "", consent: false, website: "",
};

export default function ClubForm({ config }) {
  const { t, lang } = useLang();
  const f = t.club.form;
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (patch) => setV((p) => ({ ...p, ...patch }));

  const submit = async () => {
    const e = {};
    if (!v.name?.trim()) e.name = t.form.errors.required;
    if (!v.phone?.trim()) e.phone = t.form.errors.required;
    if (!v.email?.trim()) e.email = t.form.errors.required;
    if (!v.consent) e.consent = t.form.errors.consent;
    // reuse validators for phone/email format
    const ce = validateContact({ ...v, preferred_channel: v.preferred_channel || "x" }, t);
    delete ce.preferred_channel;
    const merged = { ...ce, ...e };
    setErrors(merged);
    if (Object.keys(merged).length > 0) return;
    setSubmitting(true);
    try {
      await api.post("/leads/club", {
        name: v.name, phone: v.phone, email: v.email, city: v.city,
        owns_property: v.owns_property, interest: v.interest, message: v.message,
        preferred_channel: v.preferred_channel, consent: v.consent,
        meta: getUTM(), lang, website: "",
      });
      trackLead("club");
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return <ThankYou type="club" config={config} />;

  return (
    <div data-testid="club-form" className="space-y-5">
      <div className="flex items-start gap-2 rounded-xl bg-mist border border-brand/30 p-3.5 text-sm">
        <Crown size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand" />
        <span className="text-ink/80">{f.subtitle}</span>
      </div>

      <Field label={f.labels.name} error={errors.name}>
        <TextInput value={v.name} onChange={(x) => set({ name: x })} error={errors.name} data-testid="club-name" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={f.labels.phone} error={errors.phone}>
          <TextInput value={v.phone} onChange={(x) => set({ phone: x })} placeholder="+31 6 12345678" error={errors.phone} data-testid="club-phone" />
        </Field>
        <Field label={f.labels.email} error={errors.email}>
          <TextInput type="email" value={v.email} onChange={(x) => set({ email: x })} placeholder="ad@ornek.com" error={errors.email} data-testid="club-email" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={f.labels.city}>
          <TextInput value={v.city} onChange={(x) => set({ city: x })} placeholder="Amsterdam" data-testid="club-city" />
        </Field>
        <Field label={f.labels.interest}>
          <SelectField value={v.interest} onChange={(x) => set({ interest: x })} options={f.interestOptions} placeholder="—" testId="club-interest" />
        </Field>
      </div>
      <Field label={f.labels.owns}>
        <Pills value={v.owns_property} onChange={(x) => set({ owns_property: x })} options={f.ownsOptions} testId="club-owns" />
      </Field>
      <Field label={f.labels.channel}>
        <Pills value={v.preferred_channel} onChange={(x) => set({ preferred_channel: x })} options={t.form.options.channel} testId="club-channel" />
      </Field>
      <Field label={f.labels.message}>
        <TextArea value={v.message} onChange={(x) => set({ message: x })} />
      </Field>

      {/* honeypot */}
      <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website || ""} onChange={(e) => set({ website: e.target.value })} className="hidden" aria-hidden="true" />

      <div className="pt-1">
        <Checkbox checked={v.consent} onChange={(x) => set({ consent: x })} error={errors.consent} testId="club-consent">
          {f.labels.consent}
        </Checkbox>
      </div>

      <button type="button" onClick={submit} disabled={submitting} data-testid="club-submit"
        className="w-full h-14 px-8 rounded-xl bg-brand text-white text-base font-semibold hover:bg-brandhover shadow-md hover:shadow-lg active:scale-95 transition-[transform,box-shadow,background-color] disabled:opacity-60 flex items-center justify-center gap-2">
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? f.submitting : f.submit}
      </button>
      <p className="text-center text-xs text-stone leading-relaxed">{t.club.freeNotice}</p>
    </div>
  );
}
